import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearNewTemplateValuesRoutine,
  detachAnalyticsTemplateRoutine,
  attachAnalyticsTemplateRoutine,
  getAnalyticsSharingInvitationsRoutine,
  getAnalyticsTemplatesRoutine,
  getBpsWithAnalyticsRoutine
} from 'features/analytics/ducks/actions'
import {
  selectAnalyticsSharingInvitations,
  selectAnalyticsTemplates,
  selectBpsWithAnalytics,
  selectBpsWithAnalyticsTotalPages
} from 'features/analytics/ducks/selectors'
import styled, { css } from 'styled-components'
import PageHeader from 'components/atoms/PageHeader'
import TemplateItem from 'features/analytics/compontents/TemplateItem'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import BpDropContainer from 'features/analytics/compontents/BpDropContainer'
import { isNotNilOrEmpty } from 'utils/ramda'
import { canCurrentUserDetachTemplateFromBp } from 'features/analytics/utils/templateProvenance'
import { isEmpty, propOr } from 'ramda'
import Modal from 'components/atoms/Modal'
import SharedTemplate from 'features/analytics/compontents/SharedTemplate'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import CloseIcon from '@mui/icons-material/Close'
import TuneIcon from '@mui/icons-material/Tune'
import { useMediaQuery, useTheme, Skeleton } from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import AttachTemplateOptionsModal from 'features/analytics/compontents/AttachTemplateOptionsModal'
import AttachTemplateInfoModal from 'features/analytics/compontents/AttachTemplateInfoModal'
import { selectIsFetchingBpsWithAnalytics, selectIsLoadingAnalyticsTemplates } from 'ducks/loaderSelectors'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { toast } from 'react-toastify'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

const Analytics = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [currentPage, setCurrentPage] = useState(1)
  const templates = useSelector(selectAnalyticsTemplates)
  const currentUser = useSelector(getCurrentUser)
  const sharingInvitations = useSelector(selectAnalyticsSharingInvitations)
  const bps = useSelector(selectBpsWithAnalytics)
  const isLoadingBps = useSelector(selectIsFetchingBpsWithAnalytics)
  const isLoadingTemplates = useSelector(selectIsLoadingAnalyticsTemplates)
  const [selectedBp, setSelectedBp] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [pendingAttach, setPendingAttach] = useState(null)
  const [attachOptionsOpen, setAttachOptionsOpen] = useState(false)
  const [attachInfoOpen, setAttachInfoOpen] = useState(false)
  const [pendingAttachVisibility, setPendingAttachVisibility] = useState('shared')

  const [removeOpenModal, setRemoveModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)

  const pagesCount = useSelector(selectBpsWithAnalyticsTotalPages)
  const [showDormantBps, setShowDormantBps] = useState(false)
  const hasMoreBps = currentPage < pagesCount
  const loadMoreBps = () => setCurrentPage((p) => p + 1)
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false)

  useBodyScrollLock(isMobile && isTemplatesOpen)

  const handleOpenRemoveModal = (bp, template) => {
    setRemoveModalOpen(true)
    setSelectedBp(bp)
    setSelectedTemplate(template)
  }

  const handleCloseRemoveModal = () => {
    setRemoveModalOpen(false)
    setSelectedBp(null)
    setSelectedTemplate(null)
  }

  const handleTemplateClick = (template) => {
    if (!canUseTemplate(template)) {
      toast.info('This template is non-editable (set by the creator).')
      return
    }
    if (isMobile) {
      setSelectedTemplate(template)
      setAssignModalOpen(true)
    }
  }

  const handleAssignTemplateToBp = (bp) => {
    if (selectedTemplate && bp) {
      setPendingAttach({ template: selectedTemplate, bp })
      setAssignModalOpen(false)
      setAttachOptionsOpen(true)
    }
  }

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false)
    setSelectedTemplate(null)
  }

  const handleAttachSubmit = ({ visibility }) => {
    if (!pendingAttach) return
    setPendingAttachVisibility(visibility || 'shared')
    setAttachOptionsOpen(false)
    setAttachInfoOpen(true)
  }

  const handleAttachClose = () => {
    setAttachOptionsOpen(false)
    setAttachInfoOpen(false)
    setPendingAttach(null)
  }

  const handleAttachInfoSubmit = () => {
    if (!pendingAttach) return
    dispatch(attachAnalyticsTemplateRoutine({
      bpId: pendingAttach.bp.id,
      templateId: pendingAttach.template.id,
      page: currentPage,
      visibility: pendingAttachVisibility || 'shared',
      item_tag_requirements: [],
      template: pendingAttach.template,
      attachedBy: currentUser
    }))

    setAttachInfoOpen(false)
    setAssignModalOpen(false)
    setIsTemplatesOpen(false)
    setSelectedTemplate(null)
    setPendingAttach(null)
  }

  const bpsSortedByName = useMemo(() => {
    return isNotNilOrEmpty(bps)
      ? bps
        .filter(bp => bp.status !== 'draft')
        .filter(bp => showDormantBps || bp.status !== 'dormant')
        .sort((a, b) => a.name.localeCompare(b.name))
      : []
  }, [bps, showDormantBps])

  const customTemplates = useMemo(() => {
    return isNotNilOrEmpty(templates)
      ? templates.filter(t => t.type !== 'default')
      : []
  }, [templates])

  const getCreatorId = template =>
    template?.creator_id
    || template?.creatorId
    || template?.creator?.id
    || template?.created_by?.id
    || template?.createdBy?.id
    || template?.user_id
    || template?.userId

  const canUseTemplate = template => {
    const creatorId = getCreatorId(template)
    const isCreator = creatorId && currentUser?.id
      ? String(creatorId) === String(currentUser.id)
      : false
    const isAdmin = currentUser?.type === 'admin'
    const isLocked = !!(template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked)
    return isCreator || isAdmin || !isLocked
  }

  useEffect(() => {
    dispatch(getAnalyticsTemplatesRoutine())
    dispatch(clearNewTemplateValuesRoutine())
    dispatch(getAnalyticsSharingInvitationsRoutine())
  }, [])

  useEffect(() => {
    dispatch(getBpsWithAnalyticsRoutine({
      page: currentPage,
      includeDormant: showDormantBps
    }))
  }, [currentPage, showDormantBps])

  const handleRemoveTemplate = () => {
    if (!canCurrentUserDetachTemplateFromBp(selectedTemplate, currentUser, selectedBp)) {
      toast.info('Only the template creator can remove it from this Building Passport.')
      handleCloseRemoveModal()
      return
    }

    dispatch(
      detachAnalyticsTemplateRoutine({
        page: currentPage,
        bpId: selectedBp.id,
        templateId: selectedTemplate.id
      })
    )
    handleCloseRemoveModal()
  }

  return (
    <PageWrapper>
      <HeaderRow>
        <PageHeader>Checklists</PageHeader>
        <MobileTemplateButton onClick={() => setIsTemplatesOpen(true)}>
          <TuneIcon fontSize="small" />
          <span>Templates</span>
        </MobileTemplateButton>
      </HeaderRow>

      {isMobile ? (
        <MobileFilterTabs>
          <FilterTab
            $isActive={!showDormantBps}
            onClick={() => {
              setCurrentPage(1)
              setShowDormantBps(false)
            }}
          >
            Active Building Passports
          </FilterTab>
          <FilterTab
            $isActive={showDormantBps}
            onClick={() => {
              setCurrentPage(1)
              setShowDormantBps(true)
            }}
          >
            Dormant / Hidden
          </FilterTab>
        </MobileFilterTabs>
      ) : (
        <PageActions>
          <ToggleButton
            active={showDormantBps}
            onClick={() => {
              setCurrentPage(1)
              setShowDormantBps(!showDormantBps)
            }}
          >
            {showDormantBps ? (
              <>
                <VisibilityIcon style={{ marginRight: '8px' }} />
                Hide dormant Building Passports
              </>
            ) : (
              <>
                <VisibilityOffIcon style={{ marginRight: '8px' }} />
                Show dormant Building Passports
              </>
            )}
          </ToggleButton>
        </PageActions>
      )}

      <PageContent>
        {isLoadingBps && isEmpty(bpsSortedByName) ? (
          <LoadingWrapper>
            {[1, 2, 3].map(item => (
              <BpSkeletonCard key={item}>
                <Skeleton variant='text' width='40%' height={42} />
                <Skeleton variant='text' width='28%' height={28} />
                <Skeleton variant='rounded' width='100%' height={10} />
                <Skeleton variant='rounded' width='75%' height={10} />
              </BpSkeletonCard>
            ))}
          </LoadingWrapper>
        ) : (
          bpsSortedByName.map((bp, index) => {
            return (
              <BpDropContainer
                currentPage={currentPage}
                handleOpenRemoveModal={handleOpenRemoveModal}
                bps={bpsSortedByName}
                index={index}
                key={`bp-drop-${bp.id}`}
                bp={bp}
              />
            )
          })
        )}
        {!isEmpty(bpsSortedByName) && (
          <InfiniteScrollSentinel
            hasMore={hasMoreBps}
            loading={isLoadingBps}
            onLoadMore={loadMoreBps}
            label="Loading more Building Passports…"
          />
        )}
      </PageContent>

      <Backdrop $isOpen={isTemplatesOpen} onClick={() => setIsTemplatesOpen(false)} />

      <SidePanel $isOpen={isTemplatesOpen}>
        <BottomSheetHandle />
        <PanelHeader>
          <SidePanelTitle style={{ marginBottom: 0 }}>TEMPLATES</SidePanelTitle>
          <CloseBtn onClick={() => setIsTemplatesOpen(false)}>
            <CloseIcon />
          </CloseBtn>
        </PanelHeader>

        <SidePanelScrollContent>
          <SidePanelDescription>
            {isMobile
              ? "Tap a template to apply it to a Building Passport."
              : "Drag and drop to apply template to a Building Passport"
            }
          </SidePanelDescription>

          <SidePanelSectionTitle>
            <span>My templates</span>
            <EditIcon onClick={() => redirect(PRIVATE_PATHS.editAnalytics)} />
          </SidePanelSectionTitle>

          <SidePanelTemplatesList>
            {isLoadingTemplates ? (
              [1, 2, 3, 4, 5, 6].map(item => (
                <TemplateSkeleton key={item}>
                  <div className='content'>
                    <Skeleton variant='text' width='60%' height={32} />
                    <Skeleton variant='rounded' width='82%' height={22} />
                  </div>
                  <div className='grip'>
                    <Skeleton variant='rounded' width={24} height={48} />
                  </div>
                </TemplateSkeleton>
              ))
            ) : (
              customTemplates.map(template => {
                const canDragTemplate = canUseTemplate(template)
                return (
                  <div key={template.id} onClick={() => canDragTemplate && handleTemplateClick(template)}>
                    <TemplateItem template={template} canDrag={canDragTemplate} />
                  </div>
                )
              })
            )}
            {!isLoadingTemplates && isEmpty(customTemplates) && (
              <EmptyState>There are no custom templates</EmptyState>
            )}
          </SidePanelTemplatesList>

          <AddTemplateButton onClick={() => redirect(PRIVATE_PATHS.addAnalytics)}>
            + ADD TEMPLATE
          </AddTemplateButton>

          {!isEmpty(sharingInvitations) && (
            <PendingInvitations>
              <SidePanelSectionTitle>
                <span>Sharing invitations</span>
              </SidePanelSectionTitle>
              {sharingInvitations.map(invitation => {
                return (
                  <SharedTemplate key={invitation.id} invitation={invitation} />
                )
              })}
            </PendingInvitations>
          )}
        </SidePanelScrollContent>
      </SidePanel>

      <AttachTemplateOptionsModal
        open={attachOptionsOpen}
        onClose={handleAttachClose}
        onSubmit={handleAttachSubmit}
        templateName={pendingAttach?.template?.name}
      />

      <AttachTemplateInfoModal
        open={attachInfoOpen}
        onClose={() => {
          setAttachInfoOpen(false)
          setAttachOptionsOpen(true)
        }}
        onSubmit={handleAttachInfoSubmit}
        templateName={pendingAttach?.template?.name}
      />

      <Modal
        open={removeOpenModal}
        title='Detach template'
        onClose={handleCloseRemoveModal}
        onCancel={handleCloseRemoveModal}
        onSubmit={handleRemoveTemplate}
        withCancel
        withSubmit
        submitButtonText='Remove'
      >
        Do you really want to remove this template from{' '}
        {propOr('', 'name', selectedBp)}?
      </Modal>

      <Modal
        open={assignModalOpen}
        title={`Apply "${selectedTemplate?.name}"`}
        onClose={handleCloseAssignModal}
        withCancel={true}
        cancelButtonText="Close"
        onCancel={handleCloseAssignModal}
      >
        <AssignModalContent>
          <AssignDescription>
            Select a Building Passport to apply this checklist:
          </AssignDescription>
          <BpList>
            {bpsSortedByName.map(bp => (
              <BpListItem key={bp.id} onClick={() => handleAssignTemplateToBp(bp)}>
                <div className="info">
                  <BusinessIcon className="icon" />
                  <span className="name">{bp.name}</span>
                </div>
                <ArrowForwardIosIcon className="arrow" fontSize="small" />
              </BpListItem>
            ))}
          </BpList>
        </AssignModalContent>
      </Modal>

    </PageWrapper>
  )
}

export default Analytics

const AddTemplateButton = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  text-transform: uppercase;
  font-weight: bold;
  font-size: 11px;
  cursor: pointer;
  padding: 10px 0;
  display: inline-block;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 1024px) {
    display: block;
    width: 100%;
    text-align: center;
    padding: 14px;
    border: 1px dashed ${({ theme }) => theme.colors.secondary};
    border-radius: 8px;
    margin-top: 15px;
    font-size: 13px;
    box-sizing: border-box;
  }
`

const AssignModalContent = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 50vh;
  overflow-y: auto;
  padding-bottom: 10px;
`

const AssignDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: 12px;
`

const BpList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BpListItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.colors.grey[300]};
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background-color: ${({ theme }) => theme.colors.lighterGrey};
        border-color: ${({ theme }) => theme.colors.primary};
    }

    .info {
        display: flex;
        align-items: center;
        gap: 10px;
        overflow: hidden;

        .icon {
            color: ${({ theme }) => theme.colors.grey[500]};
        }

        .name {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.colors.haiti};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .arrow {
        color: ${({ theme }) => theme.colors.grey[400]};
        font-size: 14px;
    }
`

const PageWrapper = styled.div`
  padding-bottom: 30px;
  position: relative;
  display: flex;
  flex-direction: column;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 1024px) { margin-bottom: 16px; }
`

const MobileTemplateButton = styled.button`
  display: none;
  align-items: center;
  gap: 6px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  padding: 8px 16px;
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  @media (max-width: 1024px) { display: flex; }
`

const MobileFilterTabs = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.lighterGrey};
  padding: 4px;
  border-radius: 8px;
  margin-bottom: 20px;
  gap: 4px;
  @media (min-width: 1025px) { display: none; }
`

const FilterTab = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $isActive, theme }) => $isActive ? 'white' : 'transparent'};
  color: ${({ $isActive, theme }) => $isActive ? theme.colors.primary : theme.colors.grey[600]};
  box-shadow: ${({ $isActive }) => $isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
`

const PageContent = styled.div`
  padding-right: 300px;
  transition: all 0.3s ease;
  @media (max-width: 1024px) { padding-right: 0; width: 100%; }
`

const Backdrop = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1299;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  pointer-events: ${({ $isOpen }) => $isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s;
  display: none;
  @media (max-width: 1024px) { display: block; }
`

const SidePanel = styled.div`
  width: 280px;
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 0;
  display: flex;
  flex-direction: column;
  z-index: 100;
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    max-height: 85vh;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -5px 20px rgba(0,0,0,0.15);
    z-index: 1300;
    transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(100%)'};
  }
`

const SidePanelScrollContent = styled.div`
  padding: 25px 20px;
  overflow-y: auto;
  flex: 1;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background-color: rgba(0,0,0,0.1); border-radius: 3px; }
  @media (max-width: 1024px) { padding: 10px 20px 40px 20px; }
`

const BottomSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: ${({ theme }) => theme.colors.grey[300]};
  border-radius: 2px;
  margin: 12px auto 0 auto;
  display: none;
  flex-shrink: 0;
  @media (max-width: 1024px) { display: block; }
`

const PanelHeader = styled.div`
  display: none;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[200]};
  flex-shrink: 0;
  @media (max-width: 1024px) { display: flex; }
`

const CloseBtn = styled.div`
  padding: 5px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[600]};
  display: flex;
  align-items: center;
`

const SidePanelTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 5px;
`

const SidePanelDescription = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-bottom: 15px;
`

const SidePanelSectionTitle = styled.div`
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
  span { opacity: 0.6; }
`

const SidePanelTemplatesList = styled.div`
  padding: 10px 0;
  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const TemplateSkeleton = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 92px;

  .content {
    flex: 1;
    min-width: 0;
    margin-right: 12px;
  }

  .grip {
    width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 420px;
  width: 100%;
`

const BpSkeletonCard = styled.div`
  width: 100%;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const EditIcon = styled(EditOutlinedIcon)`
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.3s;
  &:hover { opacity: 0.8; }
`

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.grey[700]};
  padding: 15px 0;
  font-style: italic;
  font-size: 13px;
`

const PendingInvitations = styled.div`
  margin-bottom: 20px;
`

const PageActions = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 300px;
  @media (max-width: 1024px) { display: none; }
`

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid ${({ theme, active }) => active ? theme.colors.primary : theme.colors.grey[300]};
  background-color: ${({ theme, active }) => active ? `${theme.colors.primary}10` : 'transparent'};
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.grey[700]};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover { background-color: ${({ theme, active }) => active ? `${theme.colors.primary}20` : theme.colors.grey[100]}; }
  svg { font-size: 18px; }
`
